/**
 *  TecnomatChannelConfig script file
**/

import { mapActions, mapMutations, mapState } from 'vuex'
import { MODULE_NAME as MODULE_NAME_MAP, MUTATIONS as MUTATIONS_MAP } from '@/store/map/constants'
import { MODULE_NAME as MODULE_NAME_LOC, MUTATIONS as MUTATIONS_LOC, ACTIONS as ACTIONS_LOC } from '@/store/locations/constants'
import layerService from '@/api/layerService'
import { CAMPAIGN_STATUSES, CHANNEL_LABELS, CHANNEL_OPTIONS } from '@/utils/campaignOptions'
import authService, { USER_ROLES } from '@/api/authService'
import PointRadiusLayer from '@/api/PointRadiusLayer'
import { shopIconUrl } from '@/utils/mapUtils'
import campaignsService from '@/api/campaignsService'
import channelService from '@/api/channelService'
import { BUDGET_TYPE_VALUES } from '@/api/metaService'
import { multiPoint, bbox as getBbox } from '@turf/turf'
import moment from 'moment'
import googleAdsService, { MAX_ROAS_GOAL, MIN_ROAS_GOAL, STRATEGY_VALUES } from '@/api/googleAdsService'
import i18n from '@/i18n'
import { validateUrl } from '@/utils/formValidators'
import { sanitizeChannelProps } from '@/utils/channelUtils'

const defaultChannel = () => ({
  id: null,
  budget: null,
  start_date: null,
  finish_date: null,
  zones: []
})

const ZIPCODE_COLOR = [22, 150, 157, 25]
const ZIPCODE_HOVER_COLOR = [22, 150, 157, 75]
const ZIPCODE_SELECTED_COLOR = [22, 150, 157, 140]
const ZIPCODE_BORDER_COLOR = [22, 150, 157, 255]

const SHOP_LAYER_ID = 'campaign_channel_shop'
const ZONE_LAYER_ID = 'campaign_channel_zones'

export default {
  name: 'TecnomatChannelConfig',
  mixins: [],
  components: {
    MapComponent: () => import(/* webpackChunkName: "MapComponent" */ '@/components/map-component/MapComponent.vue'),
    ChannelHeader: () => import(/* webpackChunkName: "ChannelHeader" */ '@/components/channel-header/ChannelHeader.vue'),
    TecnomatGoogleChannelSidebar: () => import(/* webpackChunkName: "TecnomatGoogleChannelSidebar" */ '@/components/tecnomat-google-channel-sidebar/TecnomatGoogleChannelSidebar.vue'),
    TecnomatGoogleMetaSidebar: () => import(/* webpackChunkName: "TecnomatGoogleMetaSidebar" */ '@/components/tecnomat-meta-channel-sidebar/TecnomatMetaChannelSidebar.vue')
  },
  props: {},
  data () {
    return {
      authData: authService.authData,
      dragStart: null,
      dragCurrent: null,
      campaign: null,
      channel: defaultChannel(),
      hoveredZipcodeId: null,
      mapLoaded: false,
      boxSelectionEnabled: false,
      loading: false,
      isChannelChanged: false
    }
  },
  computed: {
    ...mapState(MODULE_NAME_LOC, [ 'viewportFilter', 'zones' ]),
    campaignid () {
      return this.$route.params.campaignid
    },
    channelid () {
      return this.$route.params.channelid
    },
    type () {
      return this.$route.params.channeltype
    },
    hasBoxSelection () {
      return this.dragStart && this.dragCurrent
    },
    boxSelectionStyles () {
      if (!this.hasBoxSelection) {
        return { display: 'none' }
      }
      const minX = Math.min(this.dragStart.x, this.dragCurrent.x)
      const minY = Math.min(this.dragStart.y, this.dragCurrent.y)
      const maxX = Math.max(this.dragStart.x, this.dragCurrent.x)
      const maxY = Math.max(this.dragStart.y, this.dragCurrent.y)
      return {
        display: 'block',
        transform: `translate(${minX}px, ${minY}px)`,
        width: `${maxX - minX - 12}px`,
        height: `${maxY - minY - 12}px`
      }
    },
    channelZones () {
      return this.channel.zones || []
    },
    allZonesSelected () {
      return this.channelZones.length === 1 && this.channelZones[0].id === 'all'
    },
    readonly () {
      const { CENTRAL_ACCEPTED, CENTRAL_REJECTED, AGENCY_CONFIRMED, FINISHED, ACTIVE } = CAMPAIGN_STATUSES
      return this.channel.id && [CENTRAL_ACCEPTED, CENTRAL_REJECTED, AGENCY_CONFIRMED, FINISHED, ACTIVE].indexOf(this.campaign.status) !== -1
    },
    areas () {
      return this.channel.zones
    },
    isAgency () {
      return this.authData.user.role === USER_ROLES.AGENCY
    },
    isAdmin () {
      return this.authData.user.role === USER_ROLES.CENTRAL_ADMIN ||
        this.authData.user.role === USER_ROLES.ROOT
    },
    isLIA () {
      return this.type === CHANNEL_OPTIONS.LOCALINVENTORYADS
    },
    isGoogleSearch () {
      return this.type === CHANNEL_OPTIONS.GOOGLESEARCHADS
    },
    isMeta () {
      return this.type === CHANNEL_OPTIONS.META
    },
    isDV360 () {
      return this.type === CHANNEL_OPTIONS.DV360
    },
    isMaximizeClicks () {
      const value = this.selectedStrategy && this.selectedStrategy.value
      return value && value === STRATEGY_VALUES.MAX_CLICKS
    },
    isROASGoal () {
      const value = this.selectedStrategy && this.selectedStrategy.value
      const choices = [STRATEGY_VALUES.ROAS_GOAL, STRATEGY_VALUES.MAX_ROAS]
      return value && choices.indexOf(value) !== -1
    },
    roasGoalValid () {
      const n = Number(this.channel.roasGoal)
      return !isNaN(n) && n >= MIN_ROAS_GOAL && n <= MAX_ROAS_GOAL
    },
    startDate () {
      return this.channel.start_date || this.campaign.start_date
    },
    finishDate () {
      return this.channel.finish_date || this.campaign.finish_date
    },
    dateRangeOk () {
      const minTime = new Date(this.campaign.start_date).getTime()
      const maxTime = new Date(this.campaign.finish_date).getTime()
      const startTime = moment(this.startDate).endOf('day').toDate().getTime()
      const endTime = moment(this.finishDate).startOf('day').toDate().getTime()
      return startTime >= minTime && endTime <= maxTime
    },
    budgetOk () {
      return !!this.channel.budget
    },
    adListOk () {
      const adList = {
        name: this.channel.ad.name,
        creative_message: this.channel.ad.creativeMessage,
        creative_name: this.channel.ad.creativeName,
        redirection_url: this.channel.ad.redirectionUrl,
        call_to_action: this.channel.campaignCta.id
      }

      // Unique ad
      if (!this.channel.ad.carousel) {
        adList.creative_title = this.channel.adList[0].title
        adList.creative_description = this.channel.adList[0].description
        adList.image_hash = this.channel.adList[0].imageHash
      } else {
        adList.carousel_list = this.channel.adList.map(ad => ({
          title: ad.title,
          description: ad.description,
          redirection_url: ad.redirectionUrl,
          image_hash: ad.imageHash
        }))
      }
    },
    formValid () {
      if (this.isLIA) {
        const merchant = this.channel.merchant && !!this.channel.merchant.value
        const strategy = this.channel.strategy && !!this.channel.strategy.value
        const priority = this.channel.priority && this.channel.priority.value >= 0

        const base = this.budgetOk && this.dateRangeOk && merchant && strategy && priority
        if (this.isROASGoal) {
          return base && this.roasGoalValid
        }
        if (this.isMaximizeClicks) {
          return base && !!this.channel.max_bid
        }

        return base
      }
      if (this.isGoogleSearch) {
        const networks = this.channel.networks || []
        const strategy = this.channel.strategy && !!this.channel.strategy.value
        const headlines = this.channel.headlines || []
        const descriptions = this.channel.descriptions || []
        const keywords = this.channel.keywords || []

        const isValidHeadLines = headlines.every(headline => headline.length > 3 && headline.length < 15)
        const isValidDescriptions = descriptions.every(description => description.length > 2 && description.length < 15)

        const base = this.budgetOk &&
          this.dateRangeOk &&
          !!networks.length &&
          strategy &&
          !!this.channel.finalURL &&
          this.validateURL(this.channel.finalURL) &&
          isValidHeadLines &&
          isValidDescriptions &&
          !!keywords.length

        if (this.isROASGoal) {
          return base && this.roasGoalValid
        }
        if (this.isMaximizeClicks) {
          return base && !!this.channel.maximumBid
        }
        if (this.isMaximizeConversions) {
          return base && !!this.channel.cpaGoal
        }

        return base
      }
      if (this.isMeta) {
        const areZones = !!(this.channel.zones && Object.keys(this.channel.zones).length > 0)
        const areFBPositions = this.channel.adSetFBPositions ? this.channel.adSetFBPositions.length > 0 : true
        const areIGPositions = this.channel.adSetIGPositions ? this.channel.adSetIGPositions.length > 0 : true
        const areAds = (!!(this.channel.ad && this.channel.ad.name) && !!(this.channel.ad && this.channel.ad.redirectionUrl) && !!(this.channel.ad && this.channel.ad.cta))
        const areAdsList = areAds &&
        (
          this.channel.ad && (this.channel.ad.carousel === true || this.channel.ad.carousel === 'true')
            ? this.channel.adList.map(ad => !!(ad.title && ad.description && ad.imageHash && ad.redirectionUrl)).reduce((a, b) => a && b, true)
            : this.channel.adList.map(ad => !!(ad.title && ad.description && ad.imageHash)).reduce((a, b) => a && b, true)
        )

        let base = this.budgetOk &&
          this.dateRangeOk &&
          areFBPositions &&
          areIGPositions &&
          areZones &&
          areAds &&
          areAdsList &&
          !!this.channel.objective &&
          !!this.channel.adSetName &&
          !!(this.channel.adSetOptimization && this.channel.adSetOptimization.value) &&
          !!(this.channel.adSetBillingEvent && this.channel.adSetBillingEvent.value) &&
          !!(this.channel.adSetPlacements && this.channel.adSetPlacements.length > 0)
        // URL + Creativity
        if (this.channel.creativity_url) {
          base &= !!validateUrl(this.channel.creativity_url)
        }
        // Carrousel urls
        if (this.channel.ad && (this.channel.ad.carousel === true || this.channel.ad.carousel === 'true')) {
          base &= this.channel.adList.map(ad => !!validateUrl(ad.redirectionUrl)).reduce((a, b) => a && b, true)
        }
        // Files
        base &= this.channel.adList && this.channel.adList.length > 0
        return Boolean(base)
      }
      return true
    }
  },
  watch: {
    hoveredZipcodeId () {
      this.updateZipcodeLayer()
    },
    'channel.zones': function (zones) {
      if (Array.isArray(zones) && layerService.hasLayer(ZONE_LAYER_ID)) {
        this.updateZipcodeLayer()
      }
    },
    'channel.shops': function (shops) {
      if (Array.isArray(shops) && layerService.hasLayer(SHOP_LAYER_ID)) {
        this.updateShopLayer()
      }
    }
  },
  async mounted () {
    try {
      await googleAdsService.login()
      const promises = [
        campaignsService.getCampaign({ id: this.campaignid }),
        this.channelid !== 'new' && channelService.getChannel({ id: this.channelid, type: this.type })
      ]
      const [campaign, channel] = await Promise.all(promises)

      if (channel) {
        const keywords = channel.keywords ? channel.keywords.map(keyword => keyword.text) : []
        this.channel = {
          ...channel,
          ...this.channel,
          ...sanitizeChannelProps(channel),
          keywords
        }
      }
      this.$nextTick(() => {
        this.campaign = campaign
      })
    } catch (err) {
      // eslint-disable-next-line
      console.error('[channel-config.js] Error loading data for channel config', err)
    }
  },
  beforeDestroy () {
    this[MUTATIONS_MAP.RESET_VIEWSTATE]()
    this[MUTATIONS_LOC.CLEAR_LOCATIONS]()
    layerService.removeLayer(SHOP_LAYER_ID)
    layerService.removeLayer(ZONE_LAYER_ID)
    this.disableBoxSelection()
  },
  methods: {
    ...mapMutations(MODULE_NAME_MAP, [ MUTATIONS_MAP.SET_VIEWSTATE, MUTATIONS_MAP.RESET_VIEWSTATE, MUTATIONS_MAP.CENTER_BBOX ]),
    ...mapMutations(MODULE_NAME_LOC, [ MUTATIONS_LOC.SET_VIEWPORT_DATA, MUTATIONS_LOC.CLEAR_LOCATIONS ]),
    ...mapActions(MODULE_NAME_LOC, [ACTIONS_LOC.FETCH_ZONES]),
    async onMapLoaded () {
      const shopids = this.campaign.shops.map(s => s.id)
      if (shopids.length > 0) {
        if (this.isMeta) {
          await this[ACTIONS_LOC.FETCH_ZONES]({ in_shop_influence_area: shopids, query: 1, reset: true, page_size: 1000 })
        }
        await this.loadMap()
      }
      this.mapLoaded = true
    },
    async loadMap () {
      if (this.zones.query) {
        await this.updateZipcodeLayer()
      }
      await this.updateShopLayer()
      this.centerMap()
    },
    async updateShopLayer () {
      if (layerService.hasLayer(SHOP_LAYER_ID)) {
        layerService.updateLayer(SHOP_LAYER_ID, {
          updateTriggers: {
            getRadius: [this.channel.shops]
          }
        })
      } else {
        layerService.addLayer({
          id: SHOP_LAYER_ID,
          data: this.campaign.shops,
          layerType: PointRadiusLayer,
          pickable: true,
          getFillColor: [22, 150, 157, 51],
          getLineColor: [22, 150, 157],
          getRadius: d => {
            return 0
            // const tab = this.selectedAreaTab || { radius: 0 }
            // if (tab.entity !== 'center') {
            //   return 0
            // }
            // if (this.campaign.shops.length < 2) {
            //   return tab.radius
            // }
            // const selected = this.channel.shops.some(p => p.id === d.id)
            // return selected ? tab.radius : 0
          },
          getPosition: d => [d.lng, d.lat],
          getIcon: () => ({
            width: 40,
            height: 40,
            url: shopIconUrl
          }),
          getTooltip ({ object }) {
            const text = `${object.name}`
            const style = {
              'background': 'var(--colorNegative)',
              color: 'var(--colorHigh)'
            }
            return { text, style, className: 'map-popup-white' }
          },
          updateTriggers: {
            getRadius: [this.channel.shops]
          }
          // onClick: this.readonly ? undefined : ({ object }) => {
          //   const tab = this.selectedAreaTab || {}
          //   if (tab.entity !== 'center' || this.campaign.shops.length < 2) {
          //     return
          //   }
          //   if (this.channel.shops.some(p => p.id === object.id)) {
          //     this.channel.shops = this.channel.shops.filter(s => s.id !== object.id)
          //   } else {
          //     this.channel.shops = this.channel.shops.concat(object)
          //   }
          // }
        })
      }
    },
    async updateZipcodeLayer () {
      if (layerService.hasLayer(ZONE_LAYER_ID)) {
        layerService.updateLayer(ZONE_LAYER_ID, {
          updateTriggers: {
            getFillColor: [this.hoveredZipcodeId, this.channelZones]
          }
        })
      } else {
        await layerService.addSQLLayer({
          id: ZONE_LAYER_ID,
          sql: this.zones.query,
          zIndex: -1,
          pickable: true,
          getLineColor: ZIPCODE_BORDER_COLOR,
          getFillColor: (object) => {
            const id = object.properties.cartodb_id
            const hovered = id === this.hoveredZipcodeId
            const selected = this.allZonesSelected || this.channelZones.find(z => z.id === id)

            if (selected) {
              return ZIPCODE_SELECTED_COLOR
            }
            if (hovered) {
              return ZIPCODE_HOVER_COLOR
            }
            return ZIPCODE_COLOR
          },
          getTooltip ({ object }) {
            const text = `${i18n.t('ZIPCODE_SHORTER')} ${object.properties.code}`
            const style = {
              'background': 'var(--colorNegative)',
              color: 'var(--colorHigh)'
            }
            return { text, style, className: 'map-popup-white' }
          },
          updateTriggers: {
            getFillColor: [this.hoveredZipcodeId, this.channelZones]
          },
          onHover: ({ object }) => {
            this.hoveredZipcodeId = (!this.readonly && object && object.properties.cartodb_id) || null
          },
          onClick: this.readonly ? undefined : ({ object }) => {
            const elem = {
              id: object.properties.cartodb_id,
              name: object.properties.name,
              code: object.properties.code
            }
            if (this.channelZones.some(z => z.id === elem.id)) {
              this.channel.zones = this.channelZones.filter(z => z.id !== elem.id)
            } else {
              this.channel.zones = this.channelZones.concat(elem)
            }
          }
        })
      }
    },
    centerMap () {
      const data = this.campaign.shops.map(p => [p.lng, p.lat])
      const geojson = multiPoint(data)
      const [minX, minY, maxX, maxY] = getBbox(geojson)
      this[MUTATIONS_MAP.CENTER_BBOX]({ bbox: [[minX, minY], [maxX, maxY]], maxZoom: 13 })
    },
    toggleBoxSelection (flag) {
      if (flag) {
        this.enableBoxSelection()
      } else {
        this.disableBoxSelection()
      }
    },
    onChannelChange (isChannelChanged) {
      this.isChannelChanged = isChannelChanged
    },
    enableBoxSelection () {
      this.boxSelectionEnabled = true
      layerService.deckInstance.setProps({
        controller: false,
        onDragStart: ({ x, y }) => {
          this.dragStart = { x, y }
        },
        onDrag: ({ x, y }) => {
          this.dragCurrent = { x, y }
        },
        onDragEnd: ({ x, y }) => {
          if (!this.hasBoxSelection) {
            return
          }
          const minX = Math.min(this.dragStart.x, x)
          const minY = Math.min(this.dragStart.y, y)
          const maxX = Math.max(this.dragStart.x, x)
          const maxY = Math.max(this.dragStart.y, y)

          const isZone = true // this.selectedAreaTab.entity === 'zone'
          const layerId = isZone ? ZONE_LAYER_ID : this.poisLayerId
          const original = isZone ? this.channel.zones : this.channel.pois
          const selection = layerService.deckInstance.pickObjects({
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
            layerIds: [layerId]
          })
            .filter(info => info.object && (
              !original.some(g => g.id === info.object.properties.cartodb_id)
            ))
            .map(info => ({
              id: info.object.properties.cartodb_id,
              name: info.object.properties.name
            }))

          const fullSelection = [...new Set(original.concat(selection))]
          if (isZone) {
            this.channel.zones = [...new Map(fullSelection.map(item => [item.id, item])).values()]
          } else {
            this.channel.pois = fullSelection
          }

          this.dragStart = null
          this.dragCurrent = null
        }
      })
    },
    disableBoxSelection () {
      this.boxSelectionEnabled = false
      layerService.deckInstance.setProps({
        controller: true,
        onDragStart: null,
        onDrag: null,
        onDragEnd: null
      })
    },
    getChannelTypeBody () {
      if (this.isLIA) {
        const shop = this.campaign.shops[0]
        const shopId = shop && shop.id
        return {
          shop_id: shopId,
          merchant_center_id: this.channel.merchant && this.channel.merchant.value,
          bid_strategy: this.channel.strategy && this.channel.strategy.value,
          priority: this.channel.priority && this.channel.priority.value,
          roas_goal: this.channel.roasGoal,
          max_bid: this.channel.maximumBid,
          is_local_inventory: this.channel.isLocalInventory
        }
      }
      if (this.isGoogleSearch) {
        const shop = this.campaign.shops[0]
        const shopId = shop && shop.id
        const keywords = (this.channel.keywords || [])
          .map(n => ({ text: n, match_type: 'phrase' }))

        return {
          shop_id: shopId,
          network_settings: (this.channel.networks || []).map(n => n.id),
          bid_strategy: this.channel.strategy && this.channel.strategy.value,
          max_bid: this.channel.maximumBid,
          target_roas: this.channel.roasGoal,
          target_cpa: this.channel.cpaGoal,
          final_url: this.channel.finalURL,
          headlines: this.channel.headlines || [],
          descriptions: this.channel.descriptions || [],
          keywords
        }
      }
      if (this.isMeta) {
        const adList = {
          name: this.channel.ad.name,
          creative_message: this.channel.ad.creativeMessage,
          creative_name: this.channel.ad.creativeName,
          redirection_url: this.channel.ad.redirectionUrl,
          call_to_action: this.channel.campaignCta.id,
          carousel: this.channel.ad.carousel
        }

        // Unique ad
        if (this.channel.ad.carousel) {
          adList.carousel_list = this.channel.adList.map(ad => ({
            title: ad.title,
            description: ad.description,
            redirection_url: ad.redirectionUrl,
            image_hash: ad.imageHash
          }))
        } else {
          adList.creative_title = this.channel.adList[0].title
          adList.creative_description = this.channel.adList[0].description
          adList.image_hash = this.channel.adList[0].imageHash
        }

        const adSetList = [
          {
            name: this.channel.adSetName,
            optimization_goal: this.channel.adSetOptimization.value,
            billing_event: this.channel.adSetBillingEvent.value,
            bid_amount: this.channel.adSetBidAmount,
            placements: this.channel.adSetPlacements.map(p => p.id),
            facebook_positions: this.channel.adSetFBPositions ? this.channel.adSetFBPositions.map(p => p.id) : [],
            instagram_positions: this.channel.adSetIGPositions ? this.channel.adSetIGPositions.map(p => p.id) : [],
            zips: this.channel.zones.map(z => z.code),
            ad_list: [adList]
          }
        ]

        return {
          is_daily_budget: this.channel.budgetType === BUDGET_TYPE_VALUES.DAILY,
          objective: this.channel.objective && this.channel.objective.value,
          ad_set_list: adSetList
        }
        // TODO
      }
      if (this.isDV360) {
        const body = {
          campaign_goal: this.channel.campaign_goal && this.channel.campaign_goal.value,
          performance_goal: this.channel.performance_goal && this.channel.performance_goal.value,
          frecuency_cap_amount: this.channel.frecuency_cap_amount,
          frecuency_cap_period: this.channel.frecuency_cap_period && this.channel.frecuency_cap_period.value,
          insertion_orders: this.channel.insertion_orders,
          line_items: this.channel.line_items
        }
        this.channel.insertion_order_id && (body.insertion_order_id = this.channel.insertion_order_id)

        return body
      }
      return null
    },
    async saveChannel () {
      this.loading = true
      try {
        const startDate = this.channel.start_date || this.campaign.start_date
        const finishDate = this.channel.finish_date || this.campaign.finish_date
        const body = {
          id: this.channel.id,
          campaign: this.campaign.id,
          name: `${CHANNEL_LABELS[this.type]} - ${this.campaign.name}`,
          channel_type: this.type,
          budget: this.channel.budget,
          start_date: startDate,
          finish_date: finishDate,
          ...this.getChannelTypeBody()
        }

        this.channel.meta_campaign_id && (body.meta_campaign_id = this.channel.meta_campaign_id)

        const channel = await channelService.saveChannel(body, this.campaign)
        if (!this.channel.id) this.channel.id = channel.id
        this.updatedChannel = true

        this.$router.replace({ path: `/campaigns/${this.campaignid}/${this.channel.id}/${this.type}` })
      } catch (err) {
        // eslint-disable-next-line
        console.error('[tecnomat-channel-config.js] Error saving channel', err)
      }

      this.updatedChannel = true
      this.loading = false
    },
    validateURL (url) {
      if (!url) return true
      const pattern = new RegExp('^(https?:\\/\\/)?' + // protocol
        '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
        '(\\#[-a-z\\d_]*)?$', 'i') // fragment locator
      return !!pattern.test(url)
    }
  }
}
